import { useEffect } from 'react';
import { ReactComponent as VIcon } from '@elementary/common-ui/assets/v-green.svg';
import {
  Alert,
  Button,
  Flex,
  FlexColumn,
  FormField,
  LoadingButton,
  PasswordField,
  TextField,
} from '@elementary/common-ui/components';
import { CircularProgress, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { ATLAN_API_KEY_PLACEHOLDER } from 'shared/constants';
import type useAtlanIntegration from 'shared/hooks/integrations/useAtlanIntegration';
import { AtlanIntegrationForm, AtlanIntegrationResponse } from 'shared/types';
import { parseIntegrationError } from 'settings/utils';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';

type AtlanIntegrationHook = ReturnType<typeof useAtlanIntegration>;

type Props = {
  open: boolean;
  onClose: () => void;
  data?: AtlanIntegrationResponse;
  isLoading: boolean;
  testMutation: AtlanIntegrationHook['testMutation'];
  saveMutation: AtlanIntegrationHook['saveMutation'];
  deleteMutation: AtlanIntegrationHook['deleteMutation'];
};

const AtlanDialog = ({
  open,
  onClose,
  data,
  isLoading,
  testMutation,
  saveMutation,
  deleteMutation,
}: Props) => {
  const { handleSubmit, register, reset, setValue, watch } =
    useForm<AtlanIntegrationForm>({
      defaultValues: {
        baseUrl: '',
        apiKey: '',
      },
    });
  const baseUrl = watch('baseUrl');
  const apiKey = watch('apiKey');

  const isConnected = !!data?.connected;
  const storedBaseUrl = data?.base_url ?? '';
  const isTokenChanged = !!apiKey && apiKey !== ATLAN_API_KEY_PLACEHOLDER;
  const isDirty = baseUrl !== storedBaseUrl || isTokenChanged;
  const hasCredentials = !!baseUrl.trim() && (isConnected || !!apiKey?.trim());
  const canSave = hasCredentials && isDirty && testMutation.isSuccess;

  useEffect(() => {
    if (!open || isLoading) return;
    reset({
      baseUrl: storedBaseUrl,
      apiKey: isConnected ? ATLAN_API_KEY_PLACEHOLDER : '',
    });
    testMutation.reset();
    saveMutation.reset();
    deleteMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isLoading, storedBaseUrl, isConnected]);

  const resetTest = () => {
    testMutation.reset();
    saveMutation.reset();
  };

  const getFormData = (data: AtlanIntegrationForm) => ({
    baseUrl: data.baseUrl.trim(),
    apiKey:
      data.apiKey && data.apiKey !== ATLAN_API_KEY_PLACEHOLDER
        ? data.apiKey.trim()
        : undefined,
  });

  const testError = parseIntegrationError(testMutation.error);
  const submitError = parseIntegrationError(
    saveMutation.error ?? deleteMutation.error,
  );

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title={isConnected ? 'Edit Atlan connection' : 'Connect Atlan'}
      description="Send Elementary metadata and health scores to Atlan."
      width={560}
      saveButtonText={isConnected ? 'Save changes' : 'Connect'}
      onSubmit={handleSubmit((data) =>
        saveMutation.mutate(getFormData(data), { onSuccess: onClose }),
      )}
      isValid={canSave}
      loading={saveMutation.isLoading}
      error={submitError}
    >
      {isLoading ? (
        <Flex sx={{ justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Flex>
      ) : (
        <FlexColumn sx={{ gap: 3 }}>
          <FormField title="Atlan base URL">
            <TextField
              size="large"
              fullWidth
              placeholder="https://your-company.atlan.com"
              {...register('baseUrl', { onChange: resetTest })}
            />
          </FormField>

          <FormField title="API token">
            <PasswordField
              size="large"
              fullWidth
              autoComplete="off"
              placeholder={
                isConnected ? 'Enter a new API token' : 'Enter your API token'
              }
              {...register('apiKey', { onChange: resetTest })}
              isFormValueDirty={apiKey !== ATLAN_API_KEY_PLACEHOLDER}
              clearFormValue={() => {
                setValue('apiKey', '');
                resetTest();
              }}
            />
          </FormField>

          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={handleSubmit((data) =>
                testMutation.mutate(getFormData(data)),
              )}
              loading={testMutation.isLoading}
              loadingPosition="center"
              disabled={!hasCredentials || testMutation.isLoading}
            >
              Test connection
            </LoadingButton>
            {testMutation.isSuccess && (
              <Flex sx={{ alignItems: 'center', gap: 1 }}>
                <VIcon width={16} />
                <Typography sx={{ color: 'status.success.bright' }}>
                  Connection verified
                </Typography>
              </Flex>
            )}
          </Flex>

          {testError && <Alert severity="error">{testError.message}</Alert>}

          {isConnected && (
            <Button
              variant="text"
              color="error"
              onClick={() =>
                deleteMutation.mutate(undefined, {
                  onSuccess: onClose,
                })
              }
              disabled={deleteMutation.isLoading}
              sx={{ alignSelf: 'flex-start' }}
            >
              Disconnect Atlan
            </Button>
          )}
        </FlexColumn>
      )}
    </IntegrationDialog>
  );
};

export default AtlanDialog;
